import { EntityKeys, QueryType } from 'utils/reactQuery'
import { useEffect, useRef } from 'react'

import { orderQueryKey } from 'dataQueries/order.query'
import { useQueryClient } from '@tanstack/react-query'

export const EXPRESS_BROADCAST_REFRESH_MS = 30_000

/**
 * Re-reads the order while the search for a creative is live and once when the countdown ends,
 * and the assignments once when the search ends, so the cards follow the broadcast rather than the reads they were built from.
 */
export function useExpressBroadcastRefresh(orderId: number, live: boolean, countdownOver: boolean) {
  const client = useQueryClient()

  useEffect(() => {
    if (!live || orderId <= 0) return
    const intervalId = setInterval(
      () => client.invalidateQueries({ queryKey: orderQueryKey(orderId) }),
      EXPRESS_BROADCAST_REFRESH_MS
    )
    return () => clearInterval(intervalId)
  }, [client, live, orderId])

  // The winner or ops now holds the lines; the assignment cards read their own queries
  const wasLive = useRef(live)
  useEffect(() => {
    if (wasLive.current && !live) {
      client.invalidateQueries({ queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET_ONE] })
    }
    wasLive.current = live
  }, [client, live])

  // A countdown already over when the order was read needs no extra read
  const countdownWasRunning = useRef(!countdownOver)
  useEffect(() => {
    if (countdownOver && countdownWasRunning.current && orderId > 0) {
      client.invalidateQueries({ queryKey: orderQueryKey(orderId) })
    }
    countdownWasRunning.current = !countdownOver
  }, [client, countdownOver, orderId])
}
